import { Client } from '@microsoft/microsoft-graph-client';
import { M365User, GroupMembership, UserLicense } from '../types';

/**
 * Service pour les appels Microsoft Graph API
 * Toutes les opérations d'offboarding côté Graph passent par ici.
 * Les opérations Exchange (boîte partagée, FullAccess, transfert, absence)
 * n'ont pas d'équivalent Graph et vivent dans exchangeService.
 */

/** Crée un client Graph authentifié avec le token fourni */
export function createGraphClient(accessToken: string): Client {
  return Client.init({
    authProvider: (done) => {
      done(null, accessToken);
    },
  });
}

/** Recherche des utilisateurs par nom ou email */
export async function searchUsers(client: Client, query: string): Promise<M365User[]> {
  // On utilise $filter avec startsWith pour le displayName et le mail
  // ConsistencyLevel: eventual est requis pour les recherches avancées
  const encodedQuery = query.replace(/'/g, "''");
  const response = await client
    .api('/users')
    .header('ConsistencyLevel', 'eventual')
    .filter(
      `startsWith(displayName,'${encodedQuery}') or startsWith(mail,'${encodedQuery}') or startsWith(userPrincipalName,'${encodedQuery}')`
    )
    .select('id,displayName,mail,userPrincipalName,jobTitle,department,companyName,accountEnabled,onPremisesSyncEnabled')
    .top(10)
    .orderby('displayName')
    .count(true)
    .get();

  return response.value;
}

/** Récupère le profil complet d'un utilisateur avec son manager et sa dernière connexion */
export async function getUserProfile(client: Client, userId: string): Promise<M365User> {
  const user = await client
    .api(`/users/${userId}`)
    .select('id,displayName,mail,userPrincipalName,jobTitle,department,companyName,accountEnabled,onPremisesSyncEnabled')
    .get();

  // Récupérer le manager (peut ne pas exister)
  try {
    const manager = await client
      .api(`/users/${userId}/manager`)
      .select('displayName,mail')
      .get();
    user.manager = { displayName: manager.displayName, mail: manager.mail };
  } catch {
    user.manager = null;
  }

  // signInActivity exige AuditLog.Read.All et une licence Entra ID P1 : appel isolé
  // pour qu'un refus dégrade l'affichage sans casser le chargement de la fiche.
  try {
    const activity = await client
      .api(`/users/${userId}`)
      .header('ConsistencyLevel', 'eventual')
      .select('signInActivity')
      .get();
    user.lastSignInDateTime = activity?.signInActivity?.lastSignInDateTime ?? null;
  } catch {
    user.lastSignInDateTime = null;
  }

  return user;
}

/**
 * Récupère les groupes dont l'utilisateur est membre.
 * Le cast /microsoft.graph.group écarte les rôles d'annuaire côté serveur, ce qui
 * évite de dépendre de la présence de @odata.type dans une réponse filtrée par $select.
 * Pagination suivie jusqu'au bout : un compte ancien dépasse facilement 100 groupes.
 */
export async function getUserGroups(client: Client, userId: string): Promise<GroupMembership[]> {
  const groups: GroupMembership[] = [];
  let response = await client
    .api(`/users/${userId}/memberOf/microsoft.graph.group`)
    .select('id,displayName,groupTypes,mailEnabled,securityEnabled,onPremisesSyncEnabled')
    .top(999)
    .get();

  while (response) {
    groups.push(...response.value);
    if (!response['@odata.nextLink']) break;
    response = await client.api(response['@odata.nextLink']).get();
  }

  return groups;
}

/**
 * Indique pourquoi un groupe ne peut pas être quitté via Graph, ou null s'il le peut.
 * Sert à la fois au retrait effectif et à l'affichage de la fiche utilisateur.
 */
export function getGroupRemovalBlocker(group: GroupMembership): string | null {
  if (group.groupTypes?.includes('DynamicMembership')) {
    // Personne ne peut retirer un membre d'un groupe dynamique, pas même un Global
    // Admin : l'appartenance est recalculée depuis la règle. On sort du groupe soit
    // parce qu'un attribut du compte ne correspond plus, soit parce que la règle change.
    return 'appartenance dynamique — sortie par changement de règle ou d\'attribut, pas par retrait';
  }
  if (group.onPremisesSyncEnabled) {
    return "synchronisé depuis l'AD local — à retirer dans l'Active Directory";
  }
  // Les listes de distribution et groupes de sécurité à extension messagerie
  // se gèrent dans Exchange, pas dans Graph, qui refuse la modification.
  if (group.mailEnabled && !group.groupTypes?.includes('Unified')) {
    return 'groupe à extension messagerie (à retirer dans Exchange)';
  }
  return null;
}

/** Vérifie si l'utilisateur est membre d'un groupe spécifique */
export async function checkGroupMembership(
  client: Client,
  userId: string,
  groupId: string
): Promise<boolean> {
  try {
    const response = await client
      .api(`/users/${userId}/checkMemberGroups`)
      .post({ groupIds: [groupId] });
    return response.value.includes(groupId);
  } catch {
    return false;
  }
}

/** Vérifie l'appartenance à plusieurs groupes en un seul appel Graph */
export async function checkMultipleGroupMemberships(
  client: Client,
  userId: string,
  groupIds: string[]
): Promise<Record<string, boolean>> {
  if (!groupIds.length) return {};
  const response = await client
    .api(`/users/${userId}/checkMemberGroups`)
    .post({ groupIds });
  const memberOf = new Set<string>(response.value as string[]);
  return Object.fromEntries(groupIds.map((id) => [id, memberOf.has(id)]));
}

// ═══════════════════════════════════════════════
// Actions d'offboarding via Graph API
// ═══════════════════════════════════════════════

/** Révoquer toutes les sessions actives */
export async function revokeSignInSessions(client: Client, userId: string): Promise<void> {
  await client.api(`/users/${userId}/revokeSignInSessions`).post({});
}

/**
 * Retirer l'utilisateur de ses groupes Entra.
 * Les groupes dynamiques, synchronisés on-premises et à extension messagerie sont
 * ignorés : Graph ne peut pas les modifier. Ils sont remontés pour affichage afin
 * que l'admin sache ce qui lui reste à faire à la main.
 */
export async function removeFromAllGroups(
  client: Client,
  userId: string,
  groups: GroupMembership[]
): Promise<{ removed: string[]; errors: string[]; skipped: string[] }> {
  const removed: string[] = [];
  const errors: string[] = [];
  const skipped: string[] = [];

  for (const group of groups) {
    const blocker = getGroupRemovalBlocker(group);
    if (blocker) {
      skipped.push(`${group.displayName} (${blocker})`);
      continue;
    }

    try {
      await client
        .api(`/groups/${group.id}/members/${userId}/$ref`)
        .delete();
      removed.push(group.displayName);
    } catch (error: any) {
      // Ignorer si déjà retiré (404)
      if (error.statusCode !== 404) {
        errors.push(`${group.displayName}: ${error.message}`);
      }
    }
  }

  return { removed, errors, skipped };
}

/** Supprimer le champ Company de l'utilisateur */
export async function clearCompanyName(client: Client, userId: string): Promise<void> {
  await client.api(`/users/${userId}`).patch({ companyName: null });
}

/** Désactiver le compte utilisateur */
export async function disableAccount(client: Client, userId: string): Promise<void> {
  await client.api(`/users/${userId}`).patch({ accountEnabled: false });
}

/** Reset du mot de passe avec un mot de passe aléatoire */
export async function resetPassword(
  client: Client,
  userId: string,
  onPremisesSyncEnabled?: boolean | null
): Promise<string> {
  if (onPremisesSyncEnabled) {
    throw new Error(
      "Compte synchronisé depuis l'AD on-premises — le mot de passe doit être réinitialisé dans l'Active Directory local, pas via Microsoft 365."
    );
  }
  // Mot de passe aléatoire fort, tiré du CSPRNG du navigateur plutôt que de
  // Math.random() qui n'offre aucune garantie cryptographique.
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
  const bytes = new Uint32Array(24);
  crypto.getRandomValues(bytes);
  let newPassword = '';
  for (let i = 0; i < bytes.length; i++) {
    newPassword += chars.charAt(bytes[i] % chars.length);
  }

  await client.api(`/users/${userId}`).patch({
    passwordProfile: {
      forceChangePasswordNextSignIn: true,
      password: newPassword,
    },
  });

  return newPassword;
}

// ═══════════════════════════════════════════════
// Licences
// ═══════════════════════════════════════════════

/**
 * Licences de l'utilisateur, avec l'origine de l'attribution.
 * licenseAssignmentStates distingue l'attribution directe de celle héritée d'un
 * groupe — distinction essentielle : une licence de groupe ne peut PAS être retirée
 * par assignLicense, elle part avec l'appartenance au groupe.
 */
export async function getUserLicenses(client: Client, userId: string): Promise<UserLicense[]> {
  const [user, skus] = await Promise.all([
    client.api(`/users/${userId}`).select('licenseAssignmentStates').get(),
    client.api('/subscribedSkus').select('skuId,skuPartNumber').get(),
  ]);

  const skuNames = new Map<string, string>(
    (skus.value || []).map((s: any) => [s.skuId, s.skuPartNumber] as [string, string])
  );

  const states = user.licenseAssignmentStates || [];
  return states.map((s: any) => ({
    skuId: s.skuId,
    skuPartNumber: skuNames.get(s.skuId) || s.skuId,
    assignedByGroup: s.assignedByGroup ?? null,
  }));
}

/**
 * Retire les licences attribuées directement.
 * À n'exécuter QU'APRÈS la conversion en boîte partagée : retirer la licence d'une
 * boîte utilisateur la place en suppression réversible et la boîte disparaît au bout
 * de 30 jours.
 */
export async function removeDirectLicenses(
  client: Client,
  userId: string,
  licenses: UserLicense[]
): Promise<{ removed: string[]; keptFromGroups: string[] }> {
  const direct = licenses.filter((l) => !l.assignedByGroup);
  const fromGroups = licenses.filter((l) => l.assignedByGroup);

  if (direct.length) {
    await client.api(`/users/${userId}/assignLicense`).post({
      addLicenses: [],
      removeLicenses: direct.map((l) => l.skuId),
    });
  }

  return {
    removed: direct.map((l) => l.skuPartNumber),
    keptFromGroups: fromGroups.map((l) => l.skuPartNumber),
  };
}
